import { SearchIcon } from "@heroicons/react/solid";
import { useOne } from "@refinedev/core";
import Button from "components/button";
import { CollapsibleSection } from "components/collapsibleSection";
import Input from "components/input";
import { IModalProps, Modal } from "components/modal"
import Select from "components/select";
import { IAttendantTemplateItem, IEventIdsItem } from "interfaces";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";

interface IAddTemplateModalProps extends IModalProps {
  onAddTempate: (data: IAttendantTemplateItem[]) => void;
  id: string;
}

interface IAttendantTemplate {
  attendant_template_name: string;
  event_id: string;
  id: string;
}
export const AddTemplateModal = ({onClose, open, onAddTempate, id}: IAddTemplateModalProps) => {
  const [selectedEvent, setSelectedEvent] = useState<{ label: string; value: string; } | null>(null);
  const [attendantTemplates, setAttendantTemplates] = useState<{ title: string; data: IAttendantTemplate[]; id: string; eventId: string }[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [openTemplateId, setOpenTemplateId] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(false);

  const { data: eventsData, isLoading: isLoadingEvents } = useOne<
    IEventIdsItem[]
  >({
    resource: "content/dropdown/events?v=2",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!trigger
    } as any,
    successNotification: (data, values, resource) => {
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
  });

  const { refetch: refetchAttendants } = useOne({
    resource: `content/attendant-templates/events`,
    id: selectedEvent?.value,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false,
    } as any,
    successNotification: (data, values, resource) => {
      const updatedAttendantTemplates = [...attendantTemplates];
      updatedAttendantTemplates.unshift({
        title: selectedEvent?.label ?? "",
        data: (data?.data ?? []) as IAttendantTemplate[],
        id: Math.random().toString(16).slice(2),
        eventId: selectedEvent?.value ?? "",
      });
      setAttendantTemplates(updatedAttendantTemplates);
      setSelectedEvent(null);
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
  });

  const { refetch: refetchTemplateDetail } = useOne({
    resource: `content/attendant-templates?ids=${selectedTemplateIds.join(",")}`,
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false,
    } as any,
    successNotification: (data, values, resource) => {
      onAddTempate((data?.data ?? []).map(((attendantData: IAttendantTemplateItem) => ({
        attendant_template_name: attendantData.attendant_template_name,
        data: attendantData.data
      }))) as IAttendantTemplateItem[]);
      onClose();
      setSelectedTemplateIds([]);

      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
  });

  useEffect(() => {
    if (open && !trigger) {
      setTrigger(true);
    }
  }, [open, trigger]);

  const _submitSearch = () => {
    if (selectedEvent) {
      refetchAttendants();
    }
  }
  const events = (eventsData?.data ?? [])
    .filter((data: any) => {
      if (data.id === id) {
        return false;
      }
      if (attendantTemplates.find(template => template.eventId === data.id)) {
        return false;
      }
      return true;
    })
    .map((event: IEventIdsItem) => ({ value: event.id, label: event.name_en }));

  const _handleSelectTemplateId = (id: string, isChecked: boolean) => {
    let updatedTemplateIds = [...selectedTemplateIds];
    if (isChecked) {
      updatedTemplateIds.push(id);
    } else {
      updatedTemplateIds = updatedTemplateIds.filter(templateId => templateId !== id);
    }
    setSelectedTemplateIds(updatedTemplateIds);
  }

  console.log(selectedEvent)

  return (
    <Modal title="Select other templates" onClose={onClose} open={open}>
      <div className="flex flex-col w-full divide divide-y divide-magnolia-600">
        <div className="flex items-center gap-x-4 py-3 sm:py-4 px-4 sm:px-6">
          <div className="flex-1 flex items-center gap-x-2.5">
            <div className="flex-1">
              <Select
                key={`event-id`}
                options={events}
                id="event-id"
                placeholder="Select Event"
                className="!rounded-sm !py-0.5"
                value={selectedEvent}
                onChange={(selected: {
                  label: string;
                  value: string;
                }) => {
                  setSelectedEvent(selected);
                }}
                isLoading={isLoadingEvents}
              />
            </div>
            <Button className="shrink-0 bg-white rounded-sm px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85" onClick={_submitSearch}>
              <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
            </Button>
          </div>
          <p className="text-xs">Selected: {selectedTemplateIds.length}</p>
        </div>
        <div className="min-h-24 flex flex-col gap-y-4 px-4 py-3 sm:py-6 sm:px-6">
          {attendantTemplates.map((template,index) => (
            <div key={index} className="border rounded-lg border-gray-300">
              <CollapsibleSection key={template.id ?? ""} label={template.title} customOpen={{
                open: openTemplateId === template.id,
                onClick: () => setOpenTemplateId(prev => prev === template.id ? null : template.id)
              }}>
                <div className="box-container flex flex-col gap-y-4">
                  {template.data.map(templateData => (
                    <div className="flex items-center justify-between px-4 py-4 border rounded-lg border-gray-300" key={templateData.id}>
                      <p className="font-medium text-sm">{templateData.attendant_template_name}</p>
                      <Input
                        type="checkbox"
                        label=""
                        id={`${templateData.id}`}
                        checked={selectedTemplateIds.includes(templateData.id) ?? false}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        _handleSelectTemplateId(
                          templateData.id, e.target.checked
                        )}    
                      />
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            </div>
          ))}
        </div>
        <div className={`w-full flex items-center justify-end py-3 sm:py-4 px-4 sm:px-6`}>
          <Button className="rounded-full disabled:bg-[#C5CCD2] px-6" onClick={() => refetchTemplateDetail()} disabled={isEmpty(selectedTemplateIds) || isEmpty(attendantTemplates)}>Add Template</Button>
        </div>
      </div>
    </Modal>
  )
}